import React from 'react';
import { ArrowRightIcon, LayersIcon, ZapIcon } from 'lucide-react';
import { providers } from '../../data/providers';
import type { ProviderId, RunMode } from '../../types/aus';
import { ProviderMark } from './ProviderMark';
interface ProviderSelectProps {
  selected: RunMode | null;
  onSelect: (mode: RunMode) => void;
  onContinue: (mode: RunMode) => void;
  lastProvider: ProviderId | null;
}
export function ProviderSelect({
  selected,
  onSelect,
  onContinue,
  lastProvider
}: ProviderSelectProps) {
  const list = [providers.fannie, providers.freddie];
  return <section aria-label="Choose an underwriting engine" className="space-y-4" data-id="element-167" data-mp="3ruk8l8beu7cy">
      <div className="grid gap-4 md:grid-cols-2" data-id="element-168" data-mp="3rwn94ma6vivx">
        {list.map(provider => {
        const isSelected = selected === provider.id;
        return <div key={provider.id} className={`group relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-all ${isSelected ? `${provider.accentBorder} ring-2 ring-offset-0 ${provider.id === 'fannie' ? 'ring-brand-100' : 'ring-graphite-200'}` : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}`} data-id="element-169" data-mp="4qvx16flmffn">
              <button type="button" aria-pressed={isSelected} onClick={() => onSelect(provider.id)} className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-170" data-mp="v9fjs2vcr9zx">
                <span className="sr-only" data-id="element-171" data-mp="1j6bdplp4fpma">Select {provider.name}</span>
              </button>

              <div className="pointer-events-none flex items-start justify-between gap-4" data-id="element-172" data-mp="1e81ft3c0iawz">
                <ProviderMark provider={provider.id} size="lg" data-id="element-173" data-mp="2bn5onbuaq2am" />
                {lastProvider === provider.id && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600" data-id="element-174" data-mp="26vxf8fsyugxg">
                    <ZapIcon className="h-3 w-3" data-id="element-175" data-mp="4zor0vnwls26" />
                    Last used
                  </span>}
              </div>

              <div className="pointer-events-none mt-4 flex-1" data-id="element-176" data-mp="3req2tsba1ri5">
                <div className="flex items-center gap-2" data-id="element-177" data-mp="3rcdix9lyl72g">
                  <h3 className="font-display text-lg font-bold tracking-tight text-graphite-800" data-id="element-178" data-mp="754vab5holr">
                    {provider.name}
                  </h3>
                  <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${provider.accentBg} ${provider.accentText}`} data-id="element-179" data-mp="3mfdcuyp404bc">
                    {provider.shortLabel}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600" data-id="element-180" data-mp="3df8h5lta2ud0">{provider.product}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-500" data-id="element-181" data-mp="16pk5ww084u3h">
                  {provider.description}
                </p>
              </div>

              <button type="button" onClick={() => onContinue(provider.id)} className={`relative mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${provider.accentSolid} ${provider.accentSolidHover} ${provider.accentRing}`} data-id="element-182" data-mp="1lr0q9vuwn5wa">
                {provider.runLabel}
                <ArrowRightIcon className="h-4 w-4" data-id="element-183" data-mp="24juvhy2q7jnb" />
              </button>
            </div>;
      })}
      </div>

      <div className={`relative flex flex-col gap-4 rounded-2xl border bg-white p-5 shadow-sm transition-all sm:flex-row sm:items-center sm:justify-between ${selected === 'dual' ? 'border-brand-300 ring-2 ring-brand-100' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}`} data-id="element-184" data-mp="2dig7tbhlu2fn">
        <button type="button" aria-pressed={selected === 'dual'} onClick={() => onSelect('dual')} className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-185" data-mp="f456vp1dv95z">
          <span className="sr-only" data-id="element-186" data-mp="pkxceusbwr2x">Select dual AUS run</span>
        </button>

        <div className="pointer-events-none flex items-start gap-4" data-id="element-187" data-mp="piuoqhenfa1d">
          <span className="flex items-center" data-id="element-188" data-mp="uj8lv4epozkm">
            <ProviderMark provider="fannie" size="md" data-id="element-189" data-mp="3tz5jjy9c46ej" />
            <span className="-ml-3 ring-2 ring-white rounded-xl" data-id="element-190" data-mp="3u0gkzrfb522h">
              <ProviderMark provider="freddie" size="md" data-id="element-191" data-mp="1ubie7f5t7bx" />
            </span>
          </span>
          <div data-id="element-192" data-mp="6txsi7zoy7gi">
            <h3 className="flex items-center gap-2 font-display text-base font-bold tracking-tight text-graphite-800" data-id="element-193" data-mp="6t4rzorbse6t">
              <LayersIcon aria-hidden="true" className="h-4 w-4 text-brand-700" data-id="element-194" data-mp="bm7ejcxoqndi" />
              Dual AUS Run
            </h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-195" data-mp="3a924i4cyly8b">
              Run both engines back to back and compare findings in a single report.
            </p>
          </div>
        </div>

        <button type="button" onClick={() => onContinue('dual')} className="relative inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-brand-700 px-4 py-2.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2" data-id="element-196" data-mp="37odxag39mtac">
          Run Both
          <ArrowRightIcon className="h-4 w-4" data-id="element-197" data-mp="2k0w188cyx91k" />
        </button>
      </div>
    </section>;
}